import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { cn } from '../../../lib/utils';
import { History, Copy, RotateCcw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

const RollbackHistoryModal = ({ open, onOpenChange, onRollbackSuccess }) => {
  const { authFetch } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rollingBack, setRollingBack] = useState(null);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authFetch(`${API}/api/patients/bulk-upload-history`);
      if (res.ok) {
        const data = await res.json();
        setEntries(data.history || []);
      } else {
        setEntries([]);
      }
    } catch {
      setEntries([]);
    }
    setLoading(false);
  }, [authFetch]);

  useEffect(() => { if (open) loadHistory(); }, [open, loadHistory]);

  const handleRollback = async (batchSession) => {
    setRollingBack(batchSession);
    try {
      const res = await authFetch(`${API}/api/patients/bulk-upload/${batchSession}/rollback`, { method: 'DELETE' });
      if (res.ok) {
        const data = await res.json();
        const deleted = data.deleted || data.deleted_count || 0;
        const failed = data.failed || 0;
        const parts = [`${deleted} deleted`];
        if (failed > 0) parts.push(`${failed} failed`);
        toast.success(`Rollback complete: ${parts.join(', ')}`);
        onRollbackSuccess?.();
        loadHistory();
      } else if (res.status === 409) {
        toast.info('This batch was already rolled back');
        loadHistory();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || err.message || `Rollback failed (${res.status})`);
      }
    } catch {
      toast.error('Network error during rollback');
    }
    setRollingBack(null);
  };

  const copySession = (session) => {
    navigator.clipboard.writeText(session).then(() => toast.success('Batch ID copied'));
  };

  const fmtDate = (ts) => {
    if (!ts) return '—';
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="rollback-history-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5" /> Import History / Rollback</DialogTitle>
          <DialogDescription>View past bulk imports and undo them if needed</DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-8 text-center">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Loading history...</p>
          </div>
        )}

        {!loading && entries.length === 0 && (
          <div className="py-8 text-center" data-testid="history-empty">
            <History className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No import history found</p>
          </div>
        )}

        {!loading && entries.length > 0 && (
          <div className="space-y-3" data-testid="history-list">
            {entries.map((entry, i) => {
              const isRolledBack = entry.rolled_back;
              const isRollingBack = rollingBack === entry.batch_session;
              return (
                <div key={`${entry.batch_session}-${entry.timestamp || i}`}
                  className={cn("rounded-lg border p-3 space-y-2", isRolledBack ? "bg-slate-50 border-slate-200" : "bg-white border-slate-200")}
                  data-testid={`history-entry-${i}`}>
                  {/* Header row */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">{fmtDate(entry.timestamp)}</span>
                      {isRolledBack && <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-200">Rolled back</Badge>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:text-slate-600"
                        onClick={() => copySession(entry.batch_session)}
                        title="Copy batch ID"
                        data-testid={`copy-session-${i}`}>
                        <Copy className="h-3 w-3" />
                      </Button>
                      <Button variant="outline" size="sm"
                        className={cn("h-7 text-xs gap-1", !isRolledBack && "text-red-600 border-red-200 hover:bg-red-50")}
                        disabled={isRolledBack || isRollingBack}
                        onClick={() => handleRollback(entry.batch_session)}
                        data-testid={`rollback-btn-${i}`}>
                        {isRollingBack ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                        {isRolledBack ? 'Rolled back' : isRollingBack ? 'Rolling back...' : 'Rollback'}
                      </Button>
                    </div>
                  </div>
                  {/* Stats row */}
                  <div className="flex items-center gap-4 text-xs">
                    <span className="font-mono text-slate-400" title={entry.batch_session}>
                      {(entry.batch_session || '').slice(0, 18)}...
                    </span>
                    <div className="flex items-center gap-3 ml-auto">
                      <span className="text-slate-600">{entry.total_rows || 0} total</span>
                      <span className="text-green-700">{entry.imported || 0} imported</span>
                      {(entry.duplicates || 0) > 0 && <span className="text-amber-600">{entry.duplicates} dups</span>}
                      {(entry.errors || 0) > 0 && <span className="text-red-600">{entry.errors} errors</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="history-close-btn">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RollbackHistoryModal;
